import { useState, type ReactNode } from 'react';
import { handled, toCollect, type Stop } from '@day';
import { onMapPage, openRoute, ui } from '../app/ui';
import { askedFor, dayOf, dayWords, distance, firstOf, money, reasonWords, streetOf, time } from '../app/words';
import { keyOf, routeTitle, type Model } from '../data/model';
import { lateBy, noPhoto, statusOf } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { toast } from '../ui/Overlays';
import * as act from '../app/actions';
import { openDialog } from '../app/dialogs';
import { newRouteWith } from '../app/menus';

/** A customer's tracking page (tracking-web/public/track.html, served at goroutr.com/t/{code}). */
export const trackingLink = (code: string) => `https://goroutr.com/t/${code}`;

export function copyText(text: string, done: string) {
  (navigator.clipboard?.writeText(text) ?? Promise.reject()).then(
    () => toast(done),
    () => toast('Couldn’t copy that. Try again.'),
  );
}

type Tab = 'details' | 'activity';

/**
 * A stop's record, beside the list on Live and beside a table elsewhere: Details and Activity, as Spoke splits them. A
 * stop that's been handled opens on what happened; one still to come opens on what it is.
 */
export function StopRecord({ m, s, close, steps, tools }: { m: Model; s: Stop; close: () => void; steps?: { prev?: () => void; next?: () => void }; tools?: ReactNode }) {
  const [picked, setPicked] = useState<Tab | null>(null);
  const tab = picked ?? (handled(s) ? 'activity' : 'details');
  return (
    <>
      <div className="stophead">
        <div className="row">
          <div className="grow">
            <div className="st ellipsis" data-tip={s.address}>
              {streetOf(s)}
            </div>
            <div className="cap ellipsis">{[s.area, s.address.split(',').slice(2, 3)[0]?.trim()].filter(Boolean).join(', ') || s.address}</div>
          </div>
          {tools}
          {steps && (
            <>
              <button className="iconbtn sm" data-tip="Previous stop · ↑" aria-label="Previous stop" disabled={!steps.prev} onClick={steps.prev}>
                <Icon name="caretUp" />
              </button>
              <button className="iconbtn sm" data-tip="Next stop · ↓" aria-label="Next stop" disabled={!steps.next} onClick={steps.next}>
                <Icon name="caretDown" />
              </button>
            </>
          )}
          <button className="iconbtn sm" data-menu={`stop:${keyOf(s)}`} data-tip="Stop actions" aria-label="Stop actions">
            <Icon name="dotsThreeVertical" />
          </button>
          <button className="iconbtn sm" data-tip="Close · Esc" aria-label="Close" onClick={close}>
            <Icon name="x" />
          </button>
        </div>
        <div className="under" role="tablist" aria-label="This stop">
          {(['details', 'activity'] as const).map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setPicked(t)}>
              {t === 'details' ? 'Details' : 'Activity'}
            </button>
          ))}
        </div>
      </div>
      <div className="rec-b">{tab === 'details' ? <Details m={m} s={s} /> : <Activity m={m} s={s} />}</div>
    </>
  );
}

const Kv = ({ k, act, children }: { k: string; act?: ReactNode; children: ReactNode }) => (
  <div className="kv">
    <span className="k">{k}</span>
    <span className="v">{children}</span>
    {act}
  </div>
);

/** Where a stop stands, in words, with the time beside it. */
function outcome(m: Model, s: Stop): { words: string; when: string } {
  const pickup = s.type === 'pickup';
  const at = time(s.at, m.zone);
  const eta = s.eta != null ? `about ${time(s.eta, m.zone)}` : '';
  const late = lateBy(s);
  switch (s.state) {
    case 'done':
      return { words: s.name ? `${pickup ? 'Picked up from' : 'Delivered to'} ${s.name}` : pickup ? 'Picked up' : 'Delivered', when: at };
    case 'failed':
      return { words: s.reason === 'anotherDay' ? `${s.name ? firstOf(s.name) : 'The customer'} asked for ${askedFor(s.movedTo, m.today)}` : `Not delivered · ${reasonWords(s.reason)}`, when: at };
    case 'current':
      return { words: late ? `On the way · ${late} min late` : 'On the way', when: eta };
    case 'ahead':
      return { words: 'To go', when: eta };
    case 'waiting':
      return { words: `Waiting for ${m.person(s.riderId)?.first ?? 'the courier'} to start the route`, when: '' };
    case 'left':
      return { words: 'Not attempted · the route ended before it', when: '' };
  }
}

/** Money at the door on this stop, in words. */
function payWords(m: Model, s: Stop): { label: string; text: string; tone: string } | null {
  const amount = toCollect(s);
  if (!amount) return null;
  const cur = s.currency ?? m.currency;
  if (s.paidHow === 'unpaid') return { label: 'To collect', text: `${money(amount, cur)} · not paid`, tone: 'neg' };
  if (s.paidHow) {
    const into = s.paidToCourier ? `, to ${m.person(s.riderId)?.first ?? 'the courier'}` : '';
    return { label: 'Collected', text: `${money(s.paidAmount ?? amount, cur)} · ${s.paidHow === 'pos' ? 'POS' : s.paidHow}${into}`, tone: '' };
  }
  return { label: 'To collect', text: money(amount, cur), tone: '' };
}

function Details({ m, s }: { m: Model; s: Stop }) {
  const r = m.route(s.routeId);
  const p = m.person(s.riderId);
  const name = p?.first ?? 'The courier';
  const st = statusOf(s, m.today);
  const now = outcome(m, s);
  const pay = payWords(m, s);
  const late = lateBy(s);
  const who = s.name ?? (s.type === 'pickup' ? 'the pickup' : 'the customer');
  const ended = r != null && (r.state === 'done' || r.state === 'ended');
  const link = s.trackingCode && !s.handover ? trackingLink(s.trackingCode) : null;
  return (
    <>
      {(s.notes || s.access) && (
        <div className="notecard">
          <Icon name="notePencil" size="sm" />
          <span>{[s.access, s.notes].filter(Boolean).join(' · ')}</span>
        </div>
      )}
      <div className="eyebrow kvh">Stop</div>
      <Kv k="Status">
        <span className={st.tone}>
          {now.words}
          {now.when ? ` · ${now.when}` : ''}
        </span>
      </Kv>
      <Kv k="Type">{s.handover ? 'Handover' : s.type === 'pickup' ? 'Pickup' : 'Delivery'}</Kv>
      {s.deliverBy != null && (
        <Kv k="Deliver by">
          <span className={late ? 'neg' : ''}>
            {time(s.deliverBy, m.zone)}
            {late ? ` · ${late} min late` : ''}
          </span>
        </Kv>
      )}
      <Kv k="Packages">{s.packages}</Kv>
      {pay && (
        <Kv k={pay.label}>
          <span className={pay.tone}>{pay.text}</span>
        </Kv>
      )}
      {r && (
        <Kv k="Route">
          {onMapPage(ui.get().page) && ui.get().view === 'stop' && ui.get().back === 'route' ? (
            `${name} · ${routeTitle(r, m.zone)}, stop ${s.n} of ${r.stops.length}`
          ) : (
            <button className="link" data-tip="Open this route" onClick={() => openRoute(r.id)}>
              {name} · {routeTitle(r, m.zone)}, stop {s.n} of {r.stops.length}
            </button>
          )}
        </Kv>
      )}
      <div className="eyebrow kvh">{s.type === 'pickup' ? 'Pickup' : 'Customer'}</div>
      {s.name || s.phone ? (
        <>
          <Kv k="Name">{s.name ?? 'No name given'}</Kv>
          {s.phone && (
            <Kv
              k="Phone"
              act={
                <a className="mini" href={`tel:${s.phone.replace(/\s/g, '')}`}>
                  <Icon name="phone" size="sm" />
                  Call
                </a>
              }
            >
              <span className="mono">{s.phone}</span>
            </Kv>
          )}
        </>
      ) : (
        <div className="cap" style={{ margin: '4px 0 6px' }}>
          {ended ? 'Names, numbers and notes go when a route ends, as Routr’s privacy notice says.' : `No name or number was given for ${who}.`}
        </div>
      )}
      {!s.handover && (
        <Kv
          k="Tracking link"
          act={
            link && (
              <button className="mini" onClick={() => copyText(link, 'Tracking link copied')}>
                <Icon name="copy" size="sm" />
                Copy
              </button>
            )
          }
        >
          {link ? (s.linkSent ? 'Sent to the customer' : 'Not sent yet') : r?.state === 'waiting' ? `Made when ${name} starts the route` : ended ? 'Not kept once a route ends' : 'None'}
        </Kv>
      )}
      <StopActions m={m} s={s} />
    </>
  );
}

/** What can be done with a stop, as its record shows it: only what the owner may do to its route. */
function StopActions({ m, s }: { m: Model; s: Stop }) {
  const r = m.route(s.routeId);
  const p = m.person(s.riderId);
  const key = keyOf(s);
  const waiting = r?.state === 'waiting';
  const theirs = `${p?.first ?? 'The courier'} has started this route, so it changes on their phone`;
  const out = s.state === 'failed' ? act.retryOut(m, s) : null;
  const buttons: ReactNode[] = [];
  if (s.state === 'failed' && s.reason !== 'anotherDay' && r?.state === 'running' && out)
    buttons.push(
      <span key="out" className="cap">
        Try again sent to {m.person(out.riderId)?.first ?? 'a courier'}
      </span>,
    );
  else if (s.state === 'failed' && s.reason !== 'anotherDay' && r?.state === 'running')
    buttons.push(
      <button key="again" className="btn strong sm" onClick={() => openDialog({ kind: 'tryAgain', key })}>
        <Icon name="arrowsClockwise" size="sm" />
        Try again
      </button>,
    );
  if ((s.state === 'failed' && (s.reason === 'anotherDay' || r?.state !== 'running')) || s.state === 'left')
    buttons.push(
      <button key="new" className="btn strong sm" onClick={() => newRouteWith([s])}>
        <Icon name="calendarBlank" size="sm" />
        {s.reason === 'anotherDay' ? `Plan it for ${askedFor(s.movedTo, m.today)}` : 'Send it on a new route'}
      </button>,
    );
  if (s.trackingCode && !s.handover && (s.state === 'current' || s.state === 'ahead'))
    buttons.push(
      <button key="link" className="btn sm" onClick={() => act.sendLink(s)}>
        <Icon name="paperPlaneTilt" size="sm" />
        Send the link
      </button>,
    );
  if (waiting)
    buttons.push(
      <button key="move" className="btn sm" data-menu={`move:${key}`}>
        <Icon name="arrowsClockwise" size="sm" />
        Move to…
      </button>,
      <button key="off" className="btn sm danger" onClick={() => act.takeOffStops([key])}>
        <Icon name="trash" size="sm" />
        Take off
      </button>,
    );
  else if (r?.state === 'declined')
    buttons.push(
      <span key="declined" className="cap">
        {act.cantChange(m, m.raw(r.id))}
      </span>,
    );
  else if (r?.state === 'running' && (s.state === 'ahead' || s.state === 'current'))
    buttons.push(
      <span key="theirs" data-tip={theirs}>
        <button className="btn sm" disabled>
          <Icon name="arrowsClockwise" size="sm" />
          Move to…
        </button>
      </span>,
    );
  if (!buttons.length) return null;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 16 }}>
      {buttons}
    </div>
  );
}

interface Ev {
  icon: string;
  w: string;
  at: number | null;
  tone?: string;
}

/** What Routr knows happened to a stop, oldest first, from what its route and stop already keep. */
function eventsOf(m: Model, s: Stop): Ev[] {
  const r = m.route(s.routeId);
  const name = m.person(s.riderId)?.first ?? 'The courier';
  const events: Ev[] = [];
  if (r?.sentAt != null) events.push({ icon: 'paperPlaneTilt', w: `Sent to ${name}`, at: r.sentAt });
  if (r?.seenAt != null) events.push({ icon: 'eye', w: `Seen by ${name}`, at: r.seenAt });
  if (r?.declinedAt != null) events.push({ icon: 'warningCircle', w: `${name} can’t take it`, at: r.declinedAt, tone: 'neg' });
  if (r?.orderChangedAt != null) events.push({ icon: 'arrowsClockwise', w: `Order changed by ${name}`, at: r.orderChangedAt });
  if (r?.startedAt != null) events.push({ icon: 'path', w: `${name} started ${r.name ? `the ${r.name}` : 'the route'}`, at: r.startedAt });
  if (s.linkSent && !s.handover) events.push({ icon: 'link', w: 'Tracking link sent to the customer', at: null });
  // Each earlier visit with the reason the courier gave (no one there, couldn't pay), as the phone shows it.
  for (const t of s.tries) events.push({ icon: 'arrowsClockwise', w: `Tried${t.at == null ? ' earlier' : ''}${t.reason ? ` · ${reasonWords(t.reason)}` : ''}`, at: t.at });
  const pay = handled(s) ? payWords(m, s) : null;
  if (pay) events.push({ icon: 'money', w: pay.text, at: s.at, tone: pay.tone });
  if (s.state === 'done' && s.away != null && s.away > 300) events.push({ icon: 'mapPin', w: `Marked about ${distance(s.away)} from the address`, at: s.at, tone: 'neg' });
  return events;
}

function Activity({ m, s }: { m: Model; s: Stop }) {
  const st = statusOf(s, m.today);
  const now = outcome(m, s);
  const events = eventsOf(m, s).reverse();
  const dayLabel = (day: string) => (day === m.today ? 'Today' : day === dayOf(m.todayStart - 1, m.zone) ? 'Yesterday' : dayWords(day));
  const dayOfEv = (at: number | null) => (at != null ? dayOf(at, m.zone) : null);
  const days = new Set([...events.map((e) => dayOfEv(e.at)), dayOfEv(s.at)].filter(Boolean));
  let shown = dayOfEv(s.at) ?? dayOfEv(events.find((e) => e.at != null)?.at ?? null);
  return (
    <>
      {days.size > 1 && shown && <div className="eyebrow kvh first">{dayLabel(shown)}</div>}
      <div className="outcome">
        <div className={`row ${st.tone}`} style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icon name={st.icon} />
          <b className="grow">{now.words}</b>
          {now.when && <span className="mono t">{now.when}</span>}
        </div>
        {s.state === 'done' && s.photos.length > 0 && (
          <div className={`photos${s.photos.length > 1 ? ' two' : ''}`}>
            {s.photos.map((u, i) => (
              <a key={u} className="photo" href={u} target="_blank" rel="noreferrer" aria-label={`Delivery photo ${i + 1} of ${s.photos.length}`} style={{ backgroundImage: `url("${u}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                {i === 0 && (
                  <span className="photo-label">
                    <Icon name="camera" size="sm" />
                    {s.photos.length > 1 ? `${s.photos.length} photos` : 'Delivery photo'}
                  </span>
                )}
              </a>
            ))}
          </div>
        )}
        {s.state === 'done' && s.photos.length > 0 && !s.handover && (
          <button className="mini" style={{ marginTop: 8 }} data-tip="The photo and what happened, as one picture for WhatsApp" onClick={() => openDialog({ kind: 'proof', key: keyOf(s) })}>
            <Icon name="shareNetwork" size="sm" />
            Share the proof
          </button>
        )}
        {noPhoto(s) && (
          <div className="row cap" style={{ marginTop: 8, gap: 6 }}>
            <Icon name="cameraSlash" size="sm" />
            No delivery photo
          </div>
        )}
        {s.courierNote && (
          <div className="row cap" style={{ marginTop: 8, gap: 6, alignItems: 'flex-start' }}>
            <Icon name="chatText" size="sm" />
            <span>“{s.courierNote}”</span>
          </div>
        )}
      </div>
      {events.length > 0 && (
        <div className="tl">
          {events.map((e, i) => {
            const day = dayOfEv(e.at);
            const label = days.size > 1 && day && day !== shown ? day : null;
            if (label) shown = label;
            return (
              <div key={i} className={e.tone ?? ''}>
                {label && <div className="eyebrow kvh tlday">{dayLabel(label)}</div>}
                <Icon name={e.icon} size="sm" />
                <span>{e.w}</span>
                <span className="mono t">{e.at != null ? time(e.at, m.zone) : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
